import { useMemo } from 'react'

import {
  DocumentLanguageFilterType,
  DocumentSortByFiterType,
  DownloadsCategoryModel,
  DownloadsModel,
} from 'src/Libs/downloads/models/downloads.model'

export const compareStrings = (valueA: string, valueB: string, direction: 'asc' | 'desc'): number => {
  const result = valueA.localeCompare(valueB, undefined, { sensitivity: 'base' })
  return direction === 'asc' ? result : -result
}

export const compareDates = (dateA: string | undefined, dateB: string | undefined, direction: 'asc' | 'desc'): number => {
  const timestampA = dateA ? new Date(dateA).getTime() : 0
  const timestampB = dateB ? new Date(dateB).getTime() : 0

  if (Number.isNaN(timestampA) || Number.isNaN(timestampB)) {
    return 0
  }

  return direction === 'asc' ? timestampA - timestampB : timestampB - timestampA
}

export const getCategoryName = (
  categoryItem: DownloadsModel,
  language: DocumentLanguageFilterType,
  fallbackLanguage: keyof DownloadsCategoryModel['nameLanguages']
): string => {
  if (categoryItem.type === 'file') {
    return categoryItem.fileName ?? ''
  }

  const languageToUse = language === 'all' ? fallbackLanguage : language
  return categoryItem.nameLanguages[languageToUse] ?? categoryItem.nameLanguages.en ?? ''
}

export const sortCategoryChildren = (
  children: DownloadsModel[],
  sortBy: DocumentSortByFiterType,
  language: DocumentLanguageFilterType,
  fallbackLanguage: keyof DownloadsCategoryModel['nameLanguages']
): DownloadsModel[] => {
  const direction: 'asc' | 'desc' = sortBy.endsWith('_desc') ? 'desc' : 'asc'
  const field = sortBy.replace(/_(asc|desc)$/, '')

  return [...children].sort((childA, childB) => {
    if (childA.type !== childB.type) {
      return childA.type === 'category' ? -1 : 1
    }

    switch (field) {
      case 'name':
        return compareStrings(getCategoryName(childA, language, fallbackLanguage), getCategoryName(childB, language, fallbackLanguage), direction)
      case 'language':
        return compareStrings(childA.type === 'file' ? childA.language : '', childB.type === 'file' ? childB.language : '', direction)
      case 'country': {
        const countryA = childA.type === 'file' ? (childA.countryList ?? []).join(',') : ''
        const countryB = childB.type === 'file' ? (childB.countryList ?? []).join(',') : ''
        return compareStrings(countryA, countryB, direction)
      }
      case 'upload_date':
        return compareDates(childA.updatedAt ?? childA.createdAt, childB.updatedAt ?? childB.createdAt, direction)
      default:
        return 0
    }
  })
}

interface UseCategorySortingProps {
  category?: DownloadsCategoryModel
  sortBy: DocumentSortByFiterType
  language: DocumentLanguageFilterType
  fallbackLanguage: keyof DownloadsCategoryModel['nameLanguages']
}

export const useCategorySorting = ({ category, sortBy, language, fallbackLanguage }: UseCategorySortingProps): DownloadsCategoryModel | undefined => {
  return useMemo(() => {
    if (!category) {
      return category
    }

    return {
      ...category,
      children: sortCategoryChildren(category.children, sortBy, language, fallbackLanguage),
    }
  }, [category, fallbackLanguage, language, sortBy])
}
